import React from 'react';
import { useSelector } from 'react-redux';
import { Grid } from '@mui/material';
import { isArray } from 'lodash';

import { ApplicationState } from 'store/store';
import { useResponsive } from 'src/hooks/useResponsive';
import { OrderScenario } from 'components/order/model/Order';
import { Location } from 'components/location/model/Location';
import { ScenarioCard } from './ScenarioCard';

const DINE_IN_PREORDER_BACKGROUND_COLOR = '#cccccc';

type IProps = {
    merchantId: string | undefined;
    currentLocation: Location | undefined;
    isLocationRetired: boolean;
    menuLink: string | undefined;
    handleScenarioChange: (scenario: OrderScenario) => () => void;
    handleViewMenu: () => void;
};

export const ScenarioList = ({
    handleScenarioChange,
    currentLocation: location,
    merchantId,
    isLocationRetired,
    menuLink,
    handleViewMenu
}: IProps) => {
    const { isDesktop } = useResponsive();
    const web = useSelector((state: ApplicationState) => state.settings.settings?.web);

    const gridSize = React.useMemo(() => {
        if (isDesktop) {
            return {
                md: 12
            };
        }
        return {
            xs: 12,
            sm: 6,
            md: 4
        };
    }, [isDesktop]);

    const showDineInModule = React.useMemo(
        () =>
            !!location?.services?.dine_in &&
            isArray(web?.journeyPageScenarios) &&
            web?.journeyPageScenarios.includes('DINE_IN') &&
            !isLocationRetired,
        [location?.services?.dine_in, isLocationRetired, web?.journeyPageScenarios]
    );

    const showPreorderModule = React.useMemo(
        () =>
            !!location?.services?.pre_order &&
            isArray(web?.journeyPageScenarios) &&
            web?.journeyPageScenarios.includes('PREORDER') &&
            !isLocationRetired,
        [location?.services?.pre_order, isLocationRetired, web?.journeyPageScenarios]
    );

    const showOTTModule = React.useMemo(
        () => !!location?.services?.order_to_table || isLocationRetired,
        [location?.services?.order_to_table, isLocationRetired]
    );

    const showPATModule = React.useMemo(
        () => !!location?.services?.pay_at_table && !isLocationRetired,
        [location?.services?.pay_at_table, isLocationRetired]
    );

    return (
        <Grid container spacing={2} justifyContent="center">
            {showDineInModule && (
                <Grid size={gridSize}>
                    <ScenarioCard
                        backgroundColor={DINE_IN_PREORDER_BACKGROUND_COLOR}
                        backgroundImg={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_dine_in_selection`}
                        onClick={handleScenarioChange(OrderScenario.DINE_IN)}
                    />
                </Grid>
            )}
            {showPreorderModule && (
                <Grid size={gridSize}>
                    <ScenarioCard
                        backgroundColor={DINE_IN_PREORDER_BACKGROUND_COLOR}
                        // eslint-disable-next-line max-len
                        backgroundImg={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_preorder_collection_selection`}
                        onClick={handleScenarioChange(OrderScenario.PREORDER)}
                    />
                </Grid>
            )}
            {showOTTModule && (
                <Grid size={gridSize}>
                    <ScenarioCard
                        defaultImage={`${process.env.PUBLIC_URL}/images/defaults/journey/OTT.png`}
                        backgroundImg={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_ott_selection`}
                        onClick={handleScenarioChange(OrderScenario.ORDER_TO_TABLE)}
                    />
                </Grid>
            )}
            {showPATModule && (
                <Grid size={gridSize}>
                    <ScenarioCard
                        defaultImage={`${process.env.PUBLIC_URL}/images/defaults/journey/pay_my_bill.png`}
                        backgroundImg={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_pat_selection`}
                        onClick={handleScenarioChange(OrderScenario.TABLE)}
                    />
                </Grid>
            )}
            {!!menuLink && (
                <Grid size={gridSize}>
                    <ScenarioCard
                        defaultImage={`${process.env.PUBLIC_URL}/images/defaults/journey/view_menu.png`}
                        backgroundImg={`${process.env.MEDIA_URL}/tenants/${merchantId}/app_assets/web_menu_selection`}
                        onClick={handleViewMenu}
                    />
                </Grid>
            )}
        </Grid>
    );
};
